/**
 * SeaSlot - Shared Type Definitions
 * Central type definitions used across frontend and backend
 */

import type { EkanPreparation } from "../ekan";

// ============================================
// ENUMS
// ============================================

export enum BookingStatus {
  PENDING = "pending",
  CONFIRMED = "confirmed",
  CANCELLED = "cancelled",
  COMPLETED = "completed",
  INVOICED = "invoiced",
}

export enum TripType {
  SHORT = "short", // 3 hours - can be booked at 10AM or 2PM
  FULL_DAY = "full_day", // 8 hours - only one per day at 10AM
}

export enum TimeSlot {
  MORNING = "10:00", // 10:00 AM - for short trips and full day
  AFTERNOON = "14:00", // 2:00 PM - for short trips only
  EVENING = "18:30", // 6:30 PM - evening trips (custom trips; a trip must list it in timeSlots)
}

export enum UserRole {
  ADMIN = "admin",
  PARTNER = "partner",
  /** Skipper/crew: may use the logbook module only */
  CREW = "crew",
}

// ============================================
// BASE INTERFACES
// ============================================

export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// PARTNER (ORGANIZATION)
// ============================================

/**
 * Partner represents an organization/company that can make bookings.
 * Multiple users can belong to one partner, linked by domain or specific email.
 */
export interface Partner extends BaseEntity {
  name: string; // Company/organization name
  domain: string; // e.g., "tourcompany.com" - used to link users by domain
  allowedEmails?: string[]; // Specific emails allowed (e.g., personal gmail/hotmail addresses)
  country: string; // Partner's country
  isActive: boolean;
  loginEnabled?: boolean; // Whether partner users can log in (default true)
  notes?: string;
  // Contact information
  primaryContactName?: string; // Name of the primary contact person
  primaryContactEmail?: string; // Email for notifications (optional)
  primaryContactPhone?: string; // Phone number (optional)
  // Branding, managed by the agency itself (shown on its printed trip PDFs)
  logoUrl?: string; // Public URL of the uploaded logo (stored under logos/ in the image container)
  websiteUrl?: string; // The agency's website link
  // External system integration
  fortnoxId?: string; // Fortnox customer ID for invoicing
  fortnoxCardPaymentCode?: string; // Fortnox betalsätt code for kort, e.g. "CARD" — enables Stripe payment links
}

/**
 * What an agency user may read about its own partner (GET /api/me/partner):
 * name, contact details and branding only, no internal or accounting fields.
 */
export interface MyPartner {
  id: string;
  name: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  logoUrl?: string;
  websiteUrl?: string;
}

// ============================================
// USER (INDIVIDUAL)
// ============================================

/**
 * User represents an individual person who logs in.
 * Users are automatically created during OAuth sign-in and linked to a Partner via domain.
 */
export interface User extends BaseEntity {
  /** Server-managed logbook company membership; independent of booking partner. */
  tenantId?: string;
  partnerId: string; // FK to Partner
  partnerName: string; // Denormalized for display
  email: string;
  name: string;
  role: UserRole;
  isActive: boolean;
  oauthProvider: "google" | "microsoft";
  oauthId: string;
  lastLoginAt?: string;
}

/**
 * Admin user - separate from partner users
 */
export interface Admin extends BaseEntity {
  tenantId?: string;
  email: string;
  name: string;
  role: UserRole.ADMIN;
}

/**
 * Allowed domain controls which domains can register.
 * Must link to an existing Partner for users to be created.
 */
export interface AllowedDomain extends BaseEntity {
  domain: string; // e.g., "tourcompany.com"
  partnerId: string; // Required - links domain to specific partner
  partnerName: string; // Denormalized partner name for display
  notes?: string; // Optional description
  addedBy: string; // Admin ID who added this
}

// ============================================
// TRIP & SCHEDULE
// ============================================

/** Page templates a trip can use. Unset means "classic". */
export const TRIP_LAYOUTS = ["classic", "photo-led", "magazine", "compact"] as const;
export type TripLayout = (typeof TRIP_LAYOUTS)[number];

/** Where the weather forecast is wanted for a trip: a position the person has confirmed */
export interface TripDestination {
  name: string;
  latitude: number;
  longitude: number;
  mapsUrl?: string;
  source: "coordinates" | "maps_url" | "map_pick";
  /** Set by the server when the position is saved */
  verifiedAt: string;
  verifiedBy: string;
  /** Counts up whenever the position or the planned time changes */
  revision: number;
  /** Minutes after departure when the boat is at the destination (unset: the whole trip is forecast) */
  offsetMinutes?: number;
  stayMinutes?: number;
}

export interface Trip extends BaseEntity {
  name: string;
  description: string;
  longDescription?: string; // Detailed description for trip detail view
  tripType: TripType;
  durationMinutes: number;
  maxBookingsPerDay: number; // How many of this trip type can be booked per day
  maxGuests?: number; // Maximum number of guests allowed per booking
  price: number;
  currency: string;
  isActive: boolean; // If false, trip cannot be booked but still visible in admin
  isArchived?: boolean; // If true, trip is hidden from all lists (soft delete)
  imageUrl?: string; // URL to trip image (Azure Blob or base64)
  imageAlt?: string; // Alt text for the image
  galleryImages?: string[]; // Additional images for trip gallery
  // Lunch options
  lunchEnabled?: boolean; // Whether lunch can be added to this trip
  lunchPricePerGuest?: number; // Price per guest for lunch
  // Fortnox integration
  fortnoxArticleNumber?: string; // Fortnox article number for the trip
  // Partner access control
  allowedPartnerIds?: string[]; // If set, only these partners can book this trip. Empty = all partners allowed
  partnerBookable?: boolean; // false = only admins can book it and it is hidden from partners and agencies. Unset = true
  // Start slots and vessel buffers (see common/booking-overlap.ts)
  timeSlots?: TimeSlot[]; // Allowed start slots. Unset = by type: short = 10:00 + 14:00, full day = 10:00
  bufferBeforeMinutes?: number; // Vessel needed before the start (integer 0-240, unset = 0)
  bufferAfterMinutes?: number; // Vessel needed after the end (integer 0-240, unset = 0)

  // ---- Showcase / marketing content (all optional) ----
  slug?: string; // URL-friendly unique id for /trips/:slug
  tagline?: string; // One-line selling pitch under the name
  highlights?: string[]; // 3-5 "why book this" bullets
  included?: string[]; // What's included
  notIncluded?: string[]; // What's not included
  whatToBring?: string[];
  languages?: string[]; // e.g. ["sv", "en"]
  meetingPoint?: TripMeetingPoint; // Snapshot shown on trip pages; kept in sync with the linked location
  destination?: TripDestination; // Where the forecast is wanted; separate from the meeting point
  meetingPointId?: string; // Linked DepartureLocation; the server copies its details into meetingPoint
  layout?: TripLayout; // Page template for the trip page and its printout; unset = classic
  cancellationPolicy?: string;
  isFeatured?: boolean; // Pinned to the top of the catalog
  sortOrder?: number; // Lower = earlier in the catalog
  season?: { fromMonth: number; toMonth: number }; // 1-12, inclusive
  images?: TripImage[]; // Blob-hosted images; first is the cover. Replaces imageUrl/galleryImages
  translations?: Partial<Record<"sv" | "en", TripTranslation>>; // Per-language overrides of the text fields
  contentRevisionId?: string; // Latest immutable revision of the customer-facing text
}

/** Customer-facing trip text, versioned independently from booking and operation settings. */
export type TripTextContent = Pick<Trip, "name" | "description"> &
  Partial<Pick<Trip, "tagline" | "longDescription" | "highlights" | "included" | "notIncluded" | "whatToBring" | "cancellationPolicy" | "translations">>;

/** Immutable snapshot of a trip's text captured on a booking. */
export interface BookingTripContentSnapshot {
  schemaVersion: 1;
  revisionId?: string;
  locale: "sv" | "en";
  content: TripTextContent;
}

/** Immutable admin-only entry in a trip's text history. */
export interface TripContentRevision extends BaseEntity {
  tripId: string;
  revision: number;
  previousRevisionId?: string;
  restoredFromRevisionId?: string;
  content: TripTextContent;
  changedBy?: string;
}

/**
 * Body of PATCH /api/trips/:id. A field that is omitted is left unchanged; a
 * field set to `null` is removed from the trip. (JSON drops `undefined`, so
 * `null` is the only way to clear an optional field.) Required fields cannot
 * be null.
 */
export type TripPatch = { [K in keyof Trip]?: Trip[K] | null };

/** A position an admin has confirmed for a place (a departure address); the server sets who and when */
export interface PlaceCoordinates {
  latitude: number;
  longitude: number;
  mapsUrl?: string;
  source: "coordinates" | "maps_url" | "map_pick";
  verifiedAt: string;
  verifiedBy: string;
  /** Counts up when the position changes */
  revision: number;
}

/** A predefined departure address (meeting point) that trips can pick from. */
export interface DepartureLocation extends BaseEntity {
  name: string;
  address?: string;
  mapUrl?: string;
  notes?: string; // e.g. "Bridge I, look for the yacht Katieca"
  coordinates?: PlaceCoordinates; // Where the place is, for the weather forecast; older addresses have none
  isActive: boolean; // Inactive locations are hidden from the trip editor's dropdown
  sortOrder?: number;
}

/** One image in the trip image library (the file itself is in blob storage). */
export interface TripImageAsset extends BaseEntity {
  url: string; // Public URL saved on trips
  blobName?: string; // Absent for images that are not in our storage
  title: string;
  alt?: string;
  tags: string[];
  manualTags?: string[];
  aiTags?: string[];
  aiDescription?: string;
  classification?: { status: "queued" | "running" | "completed" | "failed"; jobId: string; requestedAt: string; startedAt?: string; completedAt?: string; error?: string; promptVersion?: string; deployment?: string; model?: string };
  sizeBytes?: number;
  contentType?: string;
  sourceUrl?: string; // Where it was imported from, if it was
  uploadedBy?: string;
  originalBlobName?: string;
  originalSizeBytes?: number;
  width?: number;
  height?: number;
  processingVersion?: string;
  renditions?: Record<"thumbnail" | "card" | "publishing" | "print", { url: string; blobName: string; width: number; height: number; sizeBytes: number; contentType: string }>;
}

/** A library image together with the trips that use it. */
export interface TripImageAssetWithUsage extends TripImageAsset {
  usedBy: { tripId: string; tripName: string }[];
}

export interface TripMeetingPoint {
  name: string;
  address?: string;
  mapUrl?: string;
  /** Copied from the departure address when the trip or the booking took its snapshot */
  coordinates?: PlaceCoordinates;
}

export interface TripImage {
  id: string;
  blobPath: string; // Path of the original in the trip-images container
  alt?: string;
  width: number;
  height: number;
}

/** Text fields that can be translated; a missing field falls back to the Trip's base value. */
export interface TripTranslation {
  name?: string;
  tagline?: string;
  description?: string;
  longDescription?: string;
  highlights?: string[];
  included?: string[];
  notIncluded?: string[];
  whatToBring?: string[];
  cancellationPolicy?: string;
}

/** Admin-only, read-only translation of supplied trip copy. The editor must review and save it. */
export interface TripTranslationRequest {
  source: TripTranslation;
  sourceLocale: "sv" | "en";
  targetLocale: "sv" | "en";
}

export interface TripTranslationResult {
  aiStatus: "completed" | "unavailable";
  sourceLocale: "sv" | "en";
  targetLocale: "sv" | "en";
  translation: TripTranslation;
  warnings: string[];
}

export interface DaySchedule extends BaseEntity {
  date: string; // ISO date string (YYYY-MM-DD)
  isAvailable: boolean;
  availableTrips: string[]; // Trip IDs available on this day
  notes?: string; // Admin notes for the day
}

// ============================================
// BOOKING & CUSTOMER
// ============================================

export interface Customer {
  name: string;
  country: string;
  mobile: string;
  email?: string; // Optional for confirmation
}

/** Where a booking departs from: a snapshot of a departure address, so later edits to the list never rewrite old bookings. */
export interface BookingDeparture extends TripMeetingPoint {
  locationId?: string; // The departure address it was copied from, if any
}

/**
 * The company's own details (set by an admin): printed on admin trip PDFs.
 * Same field names as MyPartner, so the printed "Offered by" block takes either.
 */
export interface CompanyDetails {
  name: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  orgNumber?: string; // Organisation number
  logoUrl?: string;
  websiteUrl?: string;
  /** Optional company defaults copied into new trip drafts; empty until configured. */
  defaultCancellationPolicySv?: string;
  defaultCancellationPolicyEn?: string;
}

/** Where partners and guests reach us when nothing else is configured. */
export const DEFAULT_SUPPORT_EMAIL = "info@vofs.se";

/** The one support address: the first address of the company contact email, else the default. */
export function supportEmailFrom(company?: Pick<CompanyDetails, "primaryContactEmail"> | null): string {
  const first = (company?.primaryContactEmail ?? "").split(/[;,]/)[0]?.trim();
  return first && /^[^\s@]+@[^\s@]+$/.test(first) ? first : DEFAULT_SUPPORT_EMAIL;
}

export * from "./tenant-health";

export interface Booking extends BaseEntity {
  tripId: string;
  tripName?: string; // Denormalized trip name (snapshot from trip at booking time)
  tripPrice?: number; // Snapshot of trip price at booking time
  tripCurrency?: string; // Snapshot of currency at booking time
  tripDurationMinutes?: number; // Snapshot of trip duration at booking time
  tripContentSnapshot?: BookingTripContentSnapshot; // Optional for backwards compatibility; never rewritten on legacy bookings
  partnerId?: string; // Optional - walk-in bookings have no partner
  partnerName?: string; // Denormalized partner name for display
  userId: string; // User who created the booking
  userName?: string; // Denormalized user name for display
  userEmail?: string; // Denormalized user email for display
  date: string; // ISO date string (YYYY-MM-DD)
  timeSlot: TimeSlot; // Time slot for the trip
  customStartTime?: string; // Custom start time (HH:mm) - admin only, overrides timeSlot
  customDurationHours?: number; // Custom duration in hours - admin only, used with customStartTime
  numberOfGuests: number; // Number of guests for this booking
  customer: Customer;
  status: BookingStatus;
  confirmationCode: string;
  notes?: string;
  departure?: BookingDeparture; // Departure place (defaults to the trip's usual meeting point)
  invoiceNumber?: string; // Invoice number when status is INVOICED
  confirmedAt?: string;
  cancelledAt?: string;
  cancelledBy?: string; // Who cancelled: 'admin' or 'user-{userId}'
  restoredAt?: string; // When a cancelled booking was restored
  // Lunch options
  includeLunch?: boolean; // Whether lunch is included in this booking
  lunchPricePerGuest?: number; // Price per guest for lunch (snapshot from trip)
  totalLunchPrice?: number; // Total lunch price (numberOfGuests * lunchPricePerGuest)
  // Extra costs
  extraCost?: number; // Additional cost added by admin
  extraCostDescription?: string; // Description of the extra cost
  // Partner tracking
  bookingReference?: string; // Partner's own reference for tracking
  // Invoice articles
  articles?: BookingArticle[]; // Articles to be included in the invoice
  // Fortnox integration
  fortnoxInvoiceNumber?: string; // Fortnox document number when invoice is created
  fortnoxInvoiceSent?: boolean; // Whether the Fortnox invoice has been sent (prevents editing)
  fortnoxInvoiceTotal?: number; // Invoice total incl. VAT (SEK) — saved on invoice creation, used for Stripe and bookkeeping
  fortnoxInvoiceStatus?: "draft" | "sent" | "paid" | "cancelled"; // Cached Fortnox invoice lifecycle status — refreshed opportunistically (booking edit checks) or via manual sync on the Reports page
  // Stripe payment
  stripeSessionId?: string; // Stripe Checkout Session ID
  stripePaymentStatus?: "pending" | "completed" | "expired" | "refunded";
}

// ============================================
// API REQUEST/RESPONSE TYPES
// ============================================

export interface CreateBookingRequest {
  tripId: string;
  contentLocale?: "sv" | "en";
  date: string;
  timeSlot: TimeSlot; // Time slot for the trip
  customStartTime?: string; // Custom start time (HH:mm) - admin only, overrides timeSlot
  customDurationHours?: number; // Custom duration in hours - admin only, used with customStartTime
  numberOfGuests: number; // Number of guests for this booking
  customer: Customer;
  notes?: string;
  partnerId?: string; // For admin bookings - can specify partner
  userId?: string; // For admin bookings - can specify which partner user made the booking
  includeLunch?: boolean; // Whether to include lunch
  extraCost?: number; // Additional cost added by admin
  extraCostDescription?: string; // Description of the extra cost
  bookingReference?: string; // Partner's own reference for tracking
  articles?: BookingArticle[]; // Additional articles for invoicing - admin only
  initialStatus?: BookingStatus.PENDING | BookingStatus.CONFIRMED; // Admin only - choose initial status
  customTripPrice?: number; // Admin only - override the trip base price for this booking
  departureLocationId?: string; // Departure place from the predefined list; omitted = the trip's usual meeting point
  overrideOverlap?: boolean; // Admin only - book although the vessel is busy with another departure
}

export interface CreateBookingResponse {
  booking: Booking;
  message: string;
}

export interface UpdateBookingStatusRequest {
  status: BookingStatus;
  notes?: string;
  invoiceNumber?: string; // Required when status is INVOICED; optional external reference when manually completing
  fortnoxInvoiceTotal?: number; // Optional external invoice amount when manually completing a confirmed booking
  overrideOverlap?: boolean; // Admin only - when reactivating a cancelled booking, accept a busy vessel
}

export interface UpdateBookingDateRequest {
  newDate: string; // New date for the booking (YYYY-MM-DD)
  tripId?: string; // Admin only - change the trip
  newTimeSlot?: TimeSlot; // Optional new time slot
  customStartTime?: string; // Custom start time (HH:mm) - admin only
  customDurationHours?: number; // Custom duration in hours - admin only
  overrideOverlap?: boolean; // Admin only - save although the vessel is busy with another departure
  notes?: string; // Booking notes
  extraCost?: number; // Additional cost added by admin
  extraCostDescription?: string; // Description of the extra cost
  includeLunch?: boolean; // Update lunch option
  numberOfGuests?: number; // Update number of guests
  articles?: BookingArticle[]; // Articles to add to the booking - admin only
  partnerId?: string; // Change partner assignment (admin only) - use empty string to convert to walk-in
  // Booking reference and guest details - admin only
  bookingReference?: string; // Partner's own reference for tracking
  customer?: {
    name?: string;
    country?: string;
    mobile?: string;
    email?: string;
  };
  tripPrice?: number; // Admin only - override the trip base price
  suppressEmailNotification?: boolean; // If true, skip email notification on update
  departureLocationId?: string | null; // Change the departure place (from the predefined list); null removes it
  /** The booking's updatedAt as the editor saw it: if the booking has been changed since, the save is refused (409 booking_changed) */
  expectedUpdatedAt?: string;
}

export interface ScheduleUpdateRequest {
  date: string;
  isAvailable: boolean;
  availableTrips: string[];
  notes?: string;
}

export interface PartnerRegistrationRequest {
  domainName: string;
  companyName: string;
}

// ============================================
// PARTNER & USER MANAGEMENT
// ============================================

export interface CreatePartnerRequest {
  name: string;
  domain: string;
  allowedEmails?: string[]; // Specific emails to allow (for personal domains like gmail.com)
  country: string;
  notes?: string;
  loginEnabled?: boolean; // Whether partner users can log in (default true)
  // Contact information
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  // External system integration
  fortnoxId?: string; // Fortnox customer ID for invoicing
  fortnoxCardPaymentCode?: string; // Fortnox betalsätt code for kort — enables Stripe payment links
}

export interface UserFilter {
  partnerId?: string;
  isActive?: boolean;
}

export interface PartnerWithUsers extends Partner {
  users: User[];
  bookingCount: number;
}

// ============================================
// API RESPONSE WRAPPERS
// ============================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  warning?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// ============================================
// EMAIL TEMPLATES
// ============================================

export enum EmailTemplateType {
  BOOKING_RECEIVED = "booking_received",
  BOOKING_CONFIRMED = "booking_confirmed",
  BOOKING_CANCELLED = "booking_cancelled",
  BOOKING_COMPLETED = "booking_completed",
  BOOKING_REMINDER = "booking_reminder",
  PAYMENT_REQUEST = "payment_request",
}

/**
 * Email template for notifications
 * Supports variable placeholders like {{customerName}}, {{confirmationCode}}, etc.
 */
export interface EmailTemplate extends BaseEntity {
  name: string; // Display name for admin
  type: EmailTemplateType; // Template type
  subject: string; // Email subject (supports placeholders)
  htmlBody: string; // HTML body (supports placeholders)
  isActive: boolean; // Only active templates are used
  description?: string; // Admin notes about this template
}

// ============================================
// FILTER & QUERY TYPES
// ============================================

export interface BookingFilter {
  startDate?: string;
  endDate?: string;
  status?: BookingStatus;
  partnerId?: string;
  userId?: string;
  tripId?: string;
  /** When true, returns minimal booking info for all bookings (for calendar availability) */
  availabilityOnly?: boolean;
}

export interface ScheduleFilter {
  startDate: string;
  endDate: string;
}

// ============================================
// AUDIT LOGS
// ============================================

export enum AuditLogType {
  LOGIN = "login",
  LOGIN_FAILED = "login_failed",
  LOGOUT = "logout",
  DATA_CREATE = "data_create",
  DATA_UPDATE = "data_update",
  DATA_DELETE = "data_delete",
  EMAIL_SENT = "email_sent",
}

/**
 * Login audit log entry - tracks user authentication events
 */
export interface LoginAuditLog extends BaseEntity {
  type: AuditLogType.LOGIN | AuditLogType.LOGIN_FAILED | AuditLogType.LOGOUT;
  userId?: string; // May be null for failed logins
  userEmail: string;
  userName?: string;
  partnerId?: string;
  partnerName?: string;
  provider: "google" | "microsoft" | "dev";
  ipAddress: string;
  userAgent: string;
  // Parsed from user agent
  browser?: string;
  browserVersion?: string;
  os?: string;
  osVersion?: string;
  device?: string; // desktop, mobile, tablet
  // Geo location (from IP lookup)
  country?: string;
  countryCode?: string;
  city?: string;
  region?: string;
  // Additional context
  success: boolean;
  failureReason?: string;
}

/**
 * Data change audit log entry - tracks all data modifications
 * Automatically populated via Cosmos DB Change Feed
 */
export interface DataAuditLog extends BaseEntity {
  type:
    | AuditLogType.DATA_CREATE
    | AuditLogType.DATA_UPDATE
    | AuditLogType.DATA_DELETE;
  entityType:
    | "booking"
    | "trip"
    | "partner"
    | "user"
    | "schedule"
    | "email_template";
  entityId: string;
  userId?: string; // Who made the change
  userEmail?: string;
  userName?: string;
  // Change details
  action: "create" | "update" | "delete";
  previousValues?: Record<string, unknown>; // For updates/deletes
  newValues?: Record<string, unknown>; // For creates/updates
  changedFields?: string[]; // List of fields that changed
  // Request context
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Email audit log entry - tracks all outbound emails
 */
export interface EmailAuditLog extends BaseEntity {
  type: AuditLogType.EMAIL_SENT;
  emailType: EmailTemplateType | string; // Type of email (from template or custom)
  recipientEmail: string; // Email recipient
  recipientName?: string; // Recipient name if available
  subject: string; // Email subject
  body: string; // Email body (HTML)
  status: "sent" | "failed" | "skipped"; // Delivery status
  errorMessage?: string; // Error details if failed
  // Booking context (if applicable)
  bookingId?: string;
  bookingReference?: string;
  // User context
  triggeredByUserId?: string;
  triggeredByUserEmail?: string;
  triggeredByUserName?: string;
  // Partner context
  partnerId?: string;
  partnerName?: string;
  // Email service metadata
  provider?: "msgraph" | "azure-communication"; // Email provider used
  messageId?: string; // Provider's message ID (if available)
  // Delivery tracking (if available from provider)
  sentAt?: string; // When the email was successfully sent
  deliveredAt?: string; // When confirmed delivered (if available)
  openedAt?: string; // When email was opened (if tracking available)
}

export interface AuditLogFilter {
  type?: AuditLogType;
  userId?: string;
  userEmail?: string;
  entityType?: string;
  entityId?: string;
  startDate?: string;
  endDate?: string;
  success?: boolean;
  recipientEmail?: string; // For email audit log filtering
  emailType?: string; // For email audit log filtering
  status?: string; // For email audit log filtering
}

// ============================================
// SHARE LINKS (Public Calendar Access)
// ============================================

/**
 * ShareLink allows anonymous users to view calendar availability
 * via a unique random URL without authentication
 */
export interface ShareLink extends BaseEntity {
  token: string; // Random unique token for URL (e.g., "abc123xyz")
  name: string; // Descriptive name (e.g., "Partner X Calendar View")
  createdByUserId: string;
  createdByUserName: string;
  partnerId?: string; // Optional: filter to specific partner's bookings
  partnerName?: string;
  isActive: boolean;
  expiresAt?: string; // Optional expiration date
  viewCount: number; // How many times the link has been accessed
  lastViewedAt?: string;
  showTrips?: boolean; // Show trip names/types on the public calendar (default off: free/busy only)
}

export interface CreateShareLinkRequest {
  name: string;
  partnerId?: string; // Optional: filter to specific partner
  expiresAt?: string; // Optional expiration
  showTrips?: boolean; // Optional: show trips on the public calendar (default off)
}

/**
 * Public calendar availability response (no sensitive data)
 */
export interface PublicCalendarDay {
  date: string;
  isAvailable: boolean;
  state: PublicDayState;
  /** Union of the slots of the bookable trips that day, free/busy only */
  slots: PublicTimeSlotStatus[];
  /** Trip details only when the share link has showTrips enabled; otherwise always [] */
  trips: PublicTripAvailability[];
}

export type PublicDayState = "open" | "partial" | "full" | "closed";

export interface PublicTripAvailability {
  tripId: string;
  tripName: string;
  tripType: TripType;
  timeSlots: PublicTimeSlotStatus[];
}

export interface PublicTimeSlotStatus {
  timeSlot: TimeSlot;
  status: "available" | "booked";
}

// ============================================
// FORTNOX INTEGRATION
// ============================================

/**
 * Article/line item that can be added to a booking for invoicing
 */
export interface BookingArticle {
  articleNumber: string; // Fortnox article number
  description: string; // Description of the article
  quantity: number;
  unitPrice: number; // Price per unit (ex VAT)
  discount?: number; // Discount percentage (0-100)
  unit?: string; // Unit of measure (e.g., "st", "tim")
  accountNumber?: number; // Fortnox account number (optional)
  vatPercent?: number; // VAT percentage (default 25)
}

/**
 * Fortnox invoice row for API
 */
export interface FortnoxInvoiceRow {
  ArticleNumber?: string;
  Description: string;
  DeliveredQuantity: number;
  Price: number;
  Discount?: number;
  Unit?: string;
  AccountNumber?: number;
  VAT?: number;
}

/**
 * Fortnox invoice create request
 */
export interface FortnoxInvoiceRequest {
  Invoice: {
    CustomerNumber: string;
    InvoiceDate?: string; // ISO date (YYYY-MM-DD)
    DueDate?: string; // ISO date (YYYY-MM-DD)
    Currency?: string;
    InvoiceRows: FortnoxInvoiceRow[];
    Comments?: string;
    YourReference?: string;
    OurReference?: string;
    DeliveryDate?: string; // ISO date (YYYY-MM-DD)
    ExternalInvoiceReference1?: string; // Booking confirmation code
    ExternalInvoiceReference2?: string; // Booking ID
    Remarks?: string;
    NotCompleted?: boolean; // true = draft invoice
    InvoiceType?: string; // "INVOICE" (default) or "CASHINVOICE" for kontantfaktura
    PaymentWay?: string; // Fortnox betalsätt code — only valid on CASHINVOICE
  };
}

/**
 * Fortnox invoice response
 */
export interface FortnoxInvoiceResponse {
  Invoice: {
    "@url": string;
    DocumentNumber: string;
    CustomerNumber: string;
    CustomerName: string;
    InvoiceDate: string;
    DueDate: string;
    Total: number;
    TotalVAT: number;
    Balance: number;
    Booked: boolean;
    Cancelled: boolean;
    Sent: boolean;
    InvoiceRows: FortnoxInvoiceRow[];
  };
}

/**
 * Create invoice from booking request
 */
export interface CreateInvoiceFromBookingRequest {
  bookingId: string;
  bookingDate: string; // Required for partition key
  invoiceDate?: string; // Invoice date (YYYY-MM-DD), defaults to trip date
  articles?: BookingArticle[]; // Additional articles to add to the invoice
  notes?: string; // Additional notes for the invoice
}

/**
 * Create invoice response
 */
export interface CreateInvoiceResponse {
  success: boolean;
  invoiceNumber?: string;
  fortnoxDocumentNumber?: string;
  error?: string;
}

/**
 * Fortnox company information
 * Retrieved from the CompanyInformation API endpoint
 */
export interface FortnoxCompanyInfo {
  CompanyName: string;
  OrganizationNumber: string;
  Address: string;
  City: string;
  ZipCode: string;
  Country: string;
  Phone: string;
  Email: string;
}

/**
 * Synced article from Fortnox
 * Stored locally to allow enabling/disabling for booking selection
 */
export interface Article extends BaseEntity {
  articleNumber: string; // Fortnox article number (unique identifier)
  description: string; // Article description
  salesPrice: number; // Price per unit (ex VAT)
  unit?: string; // Unit of measure (e.g., "st", "tim")
  vatPercent?: number; // VAT percentage
  isEnabled: boolean; // Whether available for booking selection
  lastSyncedAt: string; // When last synced from Fortnox
  fortnoxActive: boolean; // Whether active in Fortnox
}

/**
 * Create/Update Article request
 */
export interface UpdateArticleRequest {
  isEnabled: boolean;
}

/**
 * Article sync result
 */
export interface ArticleSyncResult {
  synced: number;
  created: number;
  updated: number;
  deactivated: number;
}

// ============================================
// CONTACT FORM
// ============================================

/**
 * Contact form submission from vofs.se website
 */
export interface ContactSubmission extends BaseEntity {
  name: string;
  email: string;
  phone?: string;
  experience?: string;
  country?: string;
  numberOfGuests?: number;
  preferredDate?: string;
  timeSlot?: "morning" | "afternoon" | "fullDay";
  message: string;
  consent: boolean;
  submittedAt: string;
  ipAddress?: string;
  emailSent: boolean;
  emailSentAt?: string;
  // Admin workflow fields
  status?: "new" | "read" | "replied" | "archived";
  adminNotes?: string;
  repliedAt?: string;
  aiSuggestion?: string;   // cached AI-generated reply draft
  dateAvailable?: boolean; // cached result of schedule availability check
  // AI risk + match assessment (generated on-demand, cached)
  riskLevel?: "low" | "medium" | "high";
  riskReasons?: string[];
  matchLevel?: "low" | "medium" | "high";
  matchReason?: string;
  // Direct booking pricing (computed when AI suggestion is generated, cached)
  priceMultiplier?: number;    // e.g. 1.56 — applied to partner base price
  priceFactors?: string[];     // e.g. ["Direct booking (+30%)", "High season (July) (+20%)"]
}

export interface ContactReplyRequest {
  message: string;
}

/**
 * Contact form request body
 */
export interface ContactFormRequest {
  name: string;
  email: string;
  phone?: string;
  experience?: string;
  country?: string;
  numberOfGuests?: number;
  preferredDate?: string;
  timeSlot?: "morning" | "afternoon" | "fullDay";
  message: string;
  consent: boolean;
  timestamp?: string;
}

// ============================================
// STRIPE PAYMENT
// ============================================

export interface CreateStripePaymentLinkRequest {
  bookingId: string;
  bookingDate: string;
}

export interface StripePaymentLinkResponse {
  paymentPageUrl: string; // {FRONTEND_URL}/pay/{confirmationCode}
  fortnoxInvoiceNumber: string;
  totalAmount: number | null; // SEK incl. VAT
  emailSent: boolean; // Whether payment email was successfully sent to customer
  customerEmail: string; // Customer email address the email was sent to
}

// ============================================
// LOGBOOK (Loggbok) MODULE
// ============================================
// Every logbook record carries tenantId (partition key) so the module can be
// split out or made multi-tenant later. Journal entries are append-only.

export type LogbookSeaArea = "A" | "B" | "C" | "D" | "E";

export type VesselTraffic = "domestic" | "international";
export type VesselLengthMeasure = "overall" | "hull" | "registered";

/**
 * Vessel masterdata. The rules library reads these facts to decide which rules
 * apply. A fact that is missing (or null) is "unknown" and never counts as met.
 */
export interface Vessel extends BaseEntity, EkanPreparation {
  tenantId: string;
  name: string;
  callSign?: string;
  registrationNumber?: string;
  homePort?: string;
  ownerName?: string;
  operatorName?: string;
  /** Phone number installed onboard for emergency contact/callback */
  onboardPhone?: string;
  /** Verified vessel-specific assembly point and escape route */
  emergencyMusterPoint?: string;
  /** Vessel-specific location/instructions for fire-fighting equipment */
  emergencyFirePlan?: string;
  /** Verified locations for fuel, ventilation, engine and electrical isolation */
  emergencyShutdownLocations?: string;
  /** Verified bilge pump, emergency steering and damage-control locations */
  emergencyDamageControl?: string;
  /** Primary shore-side emergency contact */
  emergencyShoreContact?: string;
  mmsi?: string;
  imo?: string;
  /** Tracker/phone device number (for future position tracking) */
  imei?: string;
  flagState?: string; // for example "SE"
  vesselType?: string; // for example "passenger_vessel"
  builtYear?: number;
  grossTonnage?: number;
  lengthM?: number;
  /** Which length lengthM is */
  lengthMeasure?: VesselLengthMeasure | null;
  enginePowerKw?: number;
  maxSpeedKn?: number;
  maxPassengers: number;
  /** The usual number of crew on board: filled in on the sailing, and confirmed there */
  defaultCrewCount?: number;
  /** Suggested master for a sailing; confirmed by the person casting off. */
  defaultMasterCrewId?: string;
  /** Crew roster proposed for each new sailing of this vessel. */
  defaultCrewAssignments?: VesselCrewAssignment[];
  /** Passengers plus crew */
  maxPersons?: number;
  /** Sea areas the certificate approves */
  seaAreas: LogbookSeaArea[];
  /** Sea areas actually sailed */
  operatedAreas?: LogbookSeaArea[];
  /** Used only in sea area E (not just on a single voyage) */
  exclusivelyAreaE?: boolean | null;
  traffic?: VesselTraffic | null;
  regularService?: boolean | null;
  /** Switch on litre/engine-hour tracking (fuel KPIs) */
  fuelTracking?: boolean;
  /** The company's own fuel facts for the installation (none is preset): capacity of the tank(s) that feed the engines */
  fuelCapacityLitres?: number;
  /** Fuel that cannot be used by the installation, if known */
  fuelUnusableLitres?: number;
  /** The company's planning reserve on top of the unusable volume (not a legal percentage) */
  fuelReserveLitres?: number;
  /** A manually given planning consumption, used only when there is no history */
  fuelConsumptionLitresPerHour?: number;
  /** When and against what the fuel facts were checked */
  fuelConfigVerifiedAt?: string;
  fuelConfigSource?: string;
  /** Raised whenever the fuel facts change; observations keep the capacity that applied */
  fuelConfigRevision?: number;
  /** When these facts were last checked against the vessel documents */
  factsCheckedAt?: string;
  safetyManningDecisionRef?: string;
  docExpiry?: string; // ISO date
  smcExpiry?: string; // ISO date
  engines: { id: string; name: string; hours: number }[];
  isActive: boolean;
}

export type CrewDuty = "master" | "deck" | "engineer" | "service" | "other";

/** A crew member's usual function on one specific vessel. */
export interface VesselCrewAssignment {
  crewMemberId: string;
  duty: CrewDuty;
}

export interface CrewMember extends BaseEntity {
  tenantId: string;
  name: string;
  userId?: string; // linked SeaSlot user with role crew
  birthDate?: string; // ISO date, used for the under-18 medical rule
  /** Usual duty aboard; independent of account permissions and a voyage's named master. */
  duty?: CrewDuty;
  isActive: boolean;
}

export enum CertificateType {
  FARTYGSBEFAL_VIII = "fartygsbefal_viii",
  MEDICAL = "medical",
  PASSENGER_SAFETY = "passenger_safety",
  VHF = "vhf",
  OTHER = "other",
}

export interface Certificate extends BaseEntity {
  tenantId: string;
  crewMemberId: string;
  type: CertificateType;
  issued?: string; // ISO date
  expires: string; // ISO date
  fileBlobPath?: string;
  notes?: string;
}

export enum EquipmentCategory {
  LIFERAFT = "liferaft",
  LIFEJACKET = "lifejacket",
  HRU = "hru",
  FLARE = "flare",
  EXTINGUISHER = "extinguisher",
  EPIRB = "epirb",
  LIFEBUOY = "lifebuoy",
  LIFEBOAT = "lifeboat",
  RESCUE_BOAT = "rescue_boat",
  RECOVERY_EQUIPMENT = "recovery_equipment",
  SART = "sart",
  VHF_RADIO = "vhf_radio",
  FIRE_PUMP = "fire_pump",
  FIRE_DETECTION = "fire_detection",
  FIXED_FIRE_SYSTEM = "fixed_fire_system",
  BILGE_PUMP = "bilge_pump",
  BILGE_ALARM = "bilge_alarm",
  FIRST_AID = "first_aid",
  NAVIGATION_LIGHTS = "navigation_lights",
  SOUND_SIGNAL = "sound_signal",
  EMERGENCY_LIGHTING = "emergency_lighting",
  ANCHOR = "anchor",
  RADAR = "radar",
  GPS_PLOTTER = "gps_plotter",
  AIS = "ais",
  AUTOPILOT = "autopilot",
  ECHO_SOUNDER = "echo_sounder",
  STEERING = "steering",
  GENERATOR = "generator",
  ELECTRICAL = "electrical",
  OTHER = "other",
}

/** Single source for equipment category lists (forms, validation, AI mapping context). */
export const EQUIPMENT_CATEGORIES: EquipmentCategory[] = Object.values(EquipmentCategory);

export interface EquipmentItem extends BaseEntity {
  tenantId: string;
  vesselId: string;
  category: EquipmentCategory;
  name: string;
  serial?: string;
  lastServiced?: string; // ISO date
  nextDue?: string; // ISO date; legacy one-date schedule
  serviceCertBlobPath?: string;
}

/** One independently scheduled control or service for an equipment item. */
export interface EquipmentServicePoint extends BaseEntity {
  tenantId: string;
  vesselId: string;
  equipmentId: string;
  title: string;
  checklistId?: string;
  requirementIds?: string[];
  firstDue?: string;
  intervalMonths?: number;
  intervalDays?: number;
  /** Calendar interval starts from the latest passed service. */
  source?: string;
  verifiedAt?: string;
  isActive: boolean;
}

/** What is known about the report of the number of persons: a reference and where it comes from */
export interface CountReport {
  /** The kind of report (for example the Maritime Single Window) */
  channel: "msw" | "other";
  /** The reference or receipt number, when there is one */
  reference?: string;
  /** When the report was made */
  reportedAt: string;
  /** "stated": the skipper says it was done. "receipt": a reference from the receiving system is entered. Neither is a check by SeaSlot. */
  basis: "stated" | "receipt";
  note?: string;
  recordedBy: string;
  recordedAt: string;
}

export type VoyageStatus = "draft" | "underway" | "completed" | "signed" | "cancelled";

/** A booking's guests as the skipper needs them before and during a trip. Never stored in the logbook. */
export interface VoyageGuest {
  bookingId: string;
  name: string;
  mobile: string;
  guests: number;
  notes?: string;
  partnerName?: string;
}

export interface Voyage extends BaseEntity {
  tenantId: string;
  vesselId: string;
  bookingIds: string[];
  /** The trip product the bookings are for (two different trips at the same time are two sailings) */
  tripId?: string;
  tripName?: string;
  status: VoyageStatus;
  date: string; // ISO date
  plannedDeparture?: string; // ISO datetime
  plannedArrival?: string;
  actualDeparture?: string;
  actualArrival?: string;
  departurePlace?: string;
  arrivalPlace?: string;
  /** Guests booked for this sailing (from the bookings); passengers is the number actually counted */
  bookedGuests?: number;
  passengers: number;
  /** People of the crew actually on board (0 until the skipper enters it) */
  crewCount: number;
  crewIds: string[];
  countConfirmedAt?: string;
  /** The bookings changed after the head count was confirmed: check again */
  bookingChangedAfterCount?: boolean;
  /** Who confirmed the head count */
  countConfirmedBy?: string;
  countConfirmedByName?: string;
  /** The master of the sailing, as named by the skipper (a crew member) */
  masterCrewId?: string;
  /** Counts up with every confirmed or new count; a count made from an older version is refused */
  countVersion?: number;
  /** The count at departure, kept as it was whatever is counted later */
  departurePassengers?: number;
  departureCrew?: number;
  /** How the count was reported to the authority, when a reference exists */
  countReport?: CountReport;
  /** Written up afterwards: the times were given by the skipper, not recorded live */
  recordedLate?: boolean;
  /** Another sailing of the same vessel overlaps this one */
  planningConflict?: string;
  mswCountReportedAt?: string;
  mswCompletionReportedAt?: string;
  engineHoursStart?: number;
  engineHoursEnd?: number;
  /** Per-engine meter values at departure and arrival. Scalar fields remain for older single-engine records. */
  engineHoursByEngine?: Record<string, { name?: string; departure?: number; arrival?: number }>;
  signedBy?: string;
  signedAt?: string;
  // Reserved for later position tracking (no migration needed)
  distanceNm?: number;
  maxDistFromDepartureNm?: number;
}

export type JournalEntryType =
  | "note"
  | "departure"
  | "arrival"
  | "incident"
  | "drill"
  | "check"
  | "bunkering"
  | "engine_hours"
  | "engine"
  | "fuel_level"
  | "evidence"
  | "correction";

export interface JournalEntry extends BaseEntity {
  tenantId: string;
  vesselId: string;
  voyageId?: string;
  type: JournalEntryType;
  text: string;
  data?: Record<string, unknown>;
  correctsEntryId?: string;
  createdBy: string; // user id
  createdByName?: string;
  signedAt: string;
  prevHash: string;
  hash: string;
}

export type DrillType = "fire" | "abandon_ship" | "man_overboard" | "other";

export interface Drill extends BaseEntity {
  tenantId: string;
  vesselId: string;
  type: DrillType;
  date: string; // ISO date
  participants: string[];
  notes?: string;
}

export interface ComplianceDeadline {
  vesselId?: string;
  /** Open task or a recorded completion, independent of days until its deadline. */
  completion?: "pending" | "completed" | "deficiency";
  reportingYear?: string;
  /** Earlier known EKAN years are complete; this row is the following year's future obligation. */
  nextPeriod?: boolean;
  kind: "certificate" | "equipment" | "vessel_document" | "ekan" | "maintenance";
  label: string;
  dueDate: string; // ISO date
  daysLeft: number;
  status: "overdue" | "due_soon" | "ok";
  refId?: string;
  servicePointId?: string;
}

// ----- Engines: hours, maintenance plan, service and faults -----

/** One maintenance task of an engine model. Intervals stay empty until someone verifies them against the maker's schedule. */
export interface EngineTask {
  /** The maker's task number (for example "W1006") or the company's own code */
  code: string;
  title: string;
  component?: string;
  hourInterval?: number;
  calendarMonths?: number;
  /** Where the task and its interval come from (document, revision, page) */
  source?: string;
  /** Who checked the interval against which document, and when. Without it the interval is "not verified". */
  verifiedBy?: string;
  verifiedAt?: string;
  verifiedAgainst?: string;
  notes?: string;
}

export interface EngineModel extends BaseEntity {
  tenantId: string;
  maker: string;
  name: string;
  tasks: EngineTask[];
}

/** What is known about a task on one engine: does it apply, and when it was last done. */
export interface EngineTaskState {
  applicable: "yes" | "no" | "unknown";
  lastDoneDate?: string;
  lastDoneHours?: number;
}

export interface Engine extends BaseEntity {
  tenantId: string;
  vesselId: string;
  modelId: string;
  /** For example "Huvudmotor SB" */
  name: string;
  serialNumber?: string;
  isActive: boolean;
  /** Known history per task code (the baseline before the logbook was used) */
  taskStates?: Record<string, EngineTaskState>;
}

export type EngineTaskStatus = "ok" | "due_soon" | "due_now" | "overdue" | "unknown" | "unverified" | "not_applicable";

/** How the engine has been run over the last twelve months: hours per month, or why that is not known */
export type HoursRate =
  | { ok: true; hoursPerMonth: number; from: string; to: string; days: number; readings: number; uncertain: string[] }
  | { ok: false; reason: string };

/**
 * When the next service is likely to be needed. It is a forecast beside the real limits and never replaces
 * them or the status of the task: the manufacturer's limits and the recorded services are the facts.
 */
export interface TaskForecast {
  hoursLeft?: number;
  /** The date the hour limit is estimated to be reached at the rate that has been seen */
  hourEstimateDate?: string;
  calendarLimit?: string;
  /** The earlier of the estimated hour date and the calendar limit */
  plannedDate?: string;
  plannedBy?: "hours" | "calendar";
  monthsApprox?: number;
  /** Planned within 30 days (a mark for planning, not a due date) */
  planSoon?: boolean;
  /** The estimate of the hours is already past: the hour limit may be reached any day */
  passedByEstimate?: boolean;
  /** Hours per month the estimate rests on */
  rate?: number;
  /** Codes that say why a part of the forecast is missing */
  reasons: string[];
  /** Codes that say why the forecast is uncertain */
  uncertain: string[];
}

export interface EngineTaskResult {
  /** Latest fully completed service; baseline imports may have no journal reference. */
  lastCompleted?: { date?: string; hours?: number; entryId?: string; recordedAt?: string; recordedBy?: string };
  code: string;
  title: string;
  component?: string;
  status: EngineTaskStatus;
  hourLimit?: number;
  hoursLeft?: number;
  calendarLimit?: string;
  daysLeft?: number;
  /** Why the status is what it is, in plain words (missing readings, unverified interval ...) */
  reasons: string[];
  /** When the next service is likely to be needed, from how the engine has been run */
  forecast?: TaskForecast;
  /** The interval has been checked against the maker's schedule. Without it the interval is a reference only and is shown as such. */
  intervalVerified?: boolean;
}

export interface EngineFault {
  entryId: string;
  at: string;
  component: string;
  description: string;
  effect: "none" | "limited" | "no_operation";
  responsible?: string;
}

export interface EngineStatus {
  engine: Engine;
  modelName: string;
  hours?: number;
  hoursAt?: string;
  /** How the engine has been run, and when the forecast was made */
  rate?: HoursRate;
  forecastAt?: string;
  tasks: EngineTaskResult[];
  openFaults: EngineFault[];
}

// ----- Logbook rules reference (Regelverk) -----
// Built-in rules live in code (backend/src/logbook/rules/rule-catalog.ts) and are
// versioned with each release. Only an admin's changes are stored: an override
// per rule, and the admin's own rules.

export type RuleArea =
  | "journal"
  | "passengers"
  | "safety_org"
  | "crew"
  | "equipment"
  | "maintenance"
  | "deadlines"
  | "operations";

/** Legal level of a rule: what kind of statement it is. */
export type RuleLevel = "krav" | "allmant_rad" | "vagledning" | "rederirutin";

/** When in the skipper's day an operational checklist is used. */
export type ChecklistMoment =
  | "before_day"
  | "before_departure"
  | "boarding"
  | "briefing"
  | "underway"
  | "arrival"
  | "bunkering"
  | "emergency_cards";

export interface RuleSource {
  label: string;
  url: string;
  kind: "pdf" | "html" | "web";
  /** Id of the stored-copy document (SourceDocument) this link belongs to, if any */
  sourceId?: string;
}

/** Vessel facts a rule condition may look at. */
export type VesselFactName =
  | "grossTonnage"
  | "lengthM"
  | "maxPassengers"
  | "maxPersons"
  | "builtYear"
  | "enginePowerKw"
  | "maxSpeedKn"
  | "seaAreas"
  | "operatedAreas"
  | "exclusivelyAreaE"
  | "traffic"
  | "regularService"
  | "flagState"
  | "vesselType";

export type RuleConditionOp = "eq" | "gt" | "gte" | "lt" | "lte" | "in" | "includesAny";

/**
 * When a rule applies, from vessel facts. A small tree, never code:
 * { all: [...] }, { any: [...] }, { not: ... } or a leaf { fact, op, value }.
 * A fact that is missing is unknown, so the answer can be "cannot be decided".
 */
export type RuleCondition =
  | { all: RuleCondition[] }
  | { any: RuleCondition[] }
  | { not: RuleCondition }
  | { fact: VesselFactName; op: RuleConditionOp; value: string | number | boolean | (string | number)[] };

export type ApplicabilityStatus = "applies" | "not_applicable" | "undetermined" | "needs_review";

/**
 * What a rule means for a vessel: mandatory (a requirement that applies to it),
 * recommended (general advice or guidance that applies), optional (a company
 * routine), or not applicable / cannot be decided / needs separate review.
 */
export type Obligation = "mandatory" | "recommended" | "optional" | "not_applicable" | "undetermined" | "needs_review";

export interface RuleApplicability {
  status: ApplicabilityStatus;
  /** Plain-language reason, shown on the Rules page */
  reason: string;
  /** Facts that are unknown and would settle it */
  missingFacts: VesselFactName[];
}

/** A per-vessel decision that a rule does not apply (with the reason). */
export interface VesselRuleSetting extends BaseEntity {
  tenantId: string;
  vesselId: string;
  ruleId: string;
  notApplicable: boolean;
  reason: string;
  /** The built-in revision this decision was made on; a newer revision re-opens it */
  basedOnRevision: number;
}

import type { RoutineRequirementRef, RoutineKind, RoutineDefinition } from "./routine-workflow";
export type * from "./routine-workflow";
export { ROUTINE_SECTION_CLASSES } from "./routine-workflow";

export interface BuiltInRule {
  id: string;
  area: RuleArea;
  level: RuleLevel;
  /** The "Nivå" sentence exactly as written, shown next to the level */
  levelNote?: string;
  /** Raised whenever the built-in text changes, so edited rules can show "updated" */
  revision: number;
  /** Sort order inside the area (from the file header) */
  order?: number;
  title: string;
  summary: string;
  why: string;
  steps: string[];
  appliesTo: string;
  frequency?: string;
  retention?: string;
  /** Who does it and when ("Ansvar och tidpunkt") */
  responsible?: string;
  /** The facts that decide whether the rule applies ("Styrande uppgifter") */
  requires?: string;
  /** Checklist items, for operational checklists */
  checklist?: string[];
  /** Requirement rule IDs this checklist supports. A link is not proof of compliance. */
  requirementIds?: string[];
  /** Stable point IDs and frozen requirement versions on document-based routines. */
  checklistItemIds?: string[];
  requirementRefs?: RoutineRequirementRef[];
  routineDefinition?: RoutineDefinition;
  documentRoutine?: { vesselId: string; routineId: string; kind: RoutineKind };
  /** What to do when an item fails ("Vid brist") */
  onFailure?: string;
  /** What to record ("Dokumentera") */
  document?: string;
  /** What SeaSlot does and does not support yet ("Systemstöd") */
  systemSupport?: string;
  /** Operational checklists only */
  moment?: ChecklistMoment;
  /** When the rule applies to a vessel; no condition means every vessel in the reviewed category */
  appliesWhen?: RuleCondition;
  /** Checklist items (1-based) that must be dealt with before cast off: a critical fault needs the master's decision */
  criticalItems?: number[];
  /** Items (1-based) that stop the sailing: not passable with the master's decision (never overlaps criticalItems) */
  blockingItems?: number[];
  sources: RuleSource[];
  /** The version of the regulation this was written from */
  basedOn: string;
  /** ISO date the text was last checked against the source */
  checkedAt: string;
  /** Set when the research was uncertain: shown as "verify with Transportstyrelsen" */
  verify?: string;
  /** Logbook places that show a "Why?" link to this rule */
  usedBy?: string[];
}

export interface RuleOverride extends BaseEntity {
  tenantId: string;
  ruleId: string;
  title?: string;
  summary?: string;
  why?: string;
  steps?: string[];
  /** The built-in revision this edit was made on top of */
  basedOnRevision: number;
}

export interface CustomRule extends BaseEntity {
  tenantId: string;
  area: RuleArea;
  title: string;
  summary: string;
  why?: string;
  steps: string[];
  sources: RuleSource[];
  /** The company's own checklist: when it is used and its items (area "operations") */
  moment?: ChecklistMoment;
  checklist?: string[];
  /** Required for new checklists; optional only for legacy records awaiting mapping. */
  requirementIds?: string[];
  criticalItems?: number[];
  blockingItems?: number[];
  /** Raised whenever the checklist or its markings change, so the company adopts the new version */
  revision?: number;
}

export interface ResolvedRule extends BuiltInRule {
  /** Set when the rules were asked for a vessel */
  applicability?: RuleApplicability;
  obligation?: Obligation;
  /** A rule that is mandatory for the vessel has been switched off: shown as a deviation until it is switched on again */
  deviation?: boolean;
  /** A per-vessel "does not apply" decision, if any (re-opened when the rule has a newer revision) */
  vesselSetting?: Pick<VesselRuleSetting, "notApplicable" | "reason" | "basedOnRevision"> & { reopened: boolean };
  origin: "built_in" | "custom";
  edited: boolean;
  /** An edited rule whose built-in text has changed since the edit */
  updatedInNewerRelease: boolean;
  /** The current built-in text, shown next to the admin's edit when updatedInNewerRelease */
  builtInOriginal?: Pick<BuiltInRule, "title" | "summary" | "why" | "steps">;
}

// ----- Vessel positions from AIS -----

/** One AIS position of a vessel. Kept as a technical recording (sjölagen 18 kap. 5 §: 3 years). */
export interface VesselPosition extends BaseEntity {
  tenantId: string;
  vesselId: string;
  mmsi: string;
  lat: number;
  lon: number;
  /** Speed over ground, knots */
  sog?: number;
  /** Course over ground, degrees */
  cog?: number;
  heading?: number;
  navStatus?: number;
  /** The time in the AIS message (UTC, ISO) */
  positionAt: string;
  receivedAt: string;
  source: "aisstream";
}

// ----- Evidence: photos and PDFs attached to the logbook -----

export type EvidenceKind = "journal" | "certificate" | "equipment" | "voyage" | "vessel_document" | "ekan";

export const VESSEL_DOCUMENT_KINDS = ["passenger_certificate", "safe_manning", "doc", "smc", "radio_licence", "insurance", "exemption", "rest_hours", "safety_manual", "self_inspection", "other"] as const;
export type VesselDocumentKind = (typeof VESSEL_DOCUMENT_KINDS)[number];

/**
 * A document the vessel carries, as the company has recorded it. The list is the company's own, made
 * from the vessel's facts and the decisions it holds: SeaSlot does not say which documents a vessel
 * must have. A recorded document, an attached file, a reviewed one and one that is valid for the use
 * are four different things; only the first three are recorded here.
 */
export interface VesselDocument extends BaseEntity {
  tenantId: string;
  vesselId: string;
  kind: VesselDocumentKind;
  title: string;
  number?: string;
  issuer?: string;
  /** What it covers (sea area, number of persons, use) */
  scope?: string;
  /** Conditions and limits in the document */
  conditions?: string;
  validFrom?: string;
  validUntil?: string;
  revision?: string;
  /** Who checked the document against the original, and when */
  verifiedAt?: string;
  verifiedBy?: string;
  /** The original (or an official electronic document) has to be shown separately: a copy here does not replace it */
  requiresOriginal?: "yes" | "no";
  isActive: boolean;
}

/** A file kept as evidence. Never deleted or replaced: a new file is a new record. */
export interface Evidence extends BaseEntity {
  tenantId: string;
  vesselId?: string;
  kind: EvidenceKind;
  /** The journal entry, certificate or equipment item it belongs to */
  refId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  /** SHA-256 of the exact bytes, to show the file has not been changed */
  sha256: string;
  blobPath: string;
  note?: string;
  uploadedBy: string;
  uploadedByName?: string;
  uploadedAt: string;
}

// ----- Checklists in use -----

export type ChecklistResult = "done" | "not_done" | "fault";

export interface ChecklistRunItem {
  id?: string;
  text: string;
  result: ChecklistResult;
  note?: string;
}

/** The structured data of a journal entry of type "check" made from a checklist. */
export interface ChecklistRunData {
  ruleId: string;
  revision: number;
  /** Snapshot of the requirement links when this signed check was recorded. */
  requirementIds?: string[];
  requirementRefs?: RoutineRequirementRef[];
  moment: ChecklistMoment;
  voyageId?: string;
  items: ChecklistRunItem[];
  hasFault: boolean;
  /** A critical item is not "done" */
  hasCriticalFault: boolean;
  /** A stop item is not "done": the sailing cannot go ahead */
  hasBlockingFault?: boolean;
  /** Exact weather evidence version shown while completing the departure assessment. */
  reviewedWeatherSnapshotId?: string;
}

/** The company's decision that a checklist template is used on a vessel (templates are not used until adopted). */
export interface VesselChecklist extends BaseEntity {
  tenantId: string;
  vesselId: string;
  ruleId: string;
  adoptedBy: string;
  adoptedByName?: string;
  adoptedAt: string;
  /** The rule revision that was adopted; a newer revision must be adopted again */
  basedOnRevision: number;
  /** Exact adopted content. Legacy adoptions without a snapshot require explicit readoption when stale. */
  snapshot?: BuiltInRule;
}

// ----- Rule library versions (shared by all companies) -----

/** One numbered version of a rule. A published version is never edited: a change is a new version. */
export interface RuleVersion extends BaseEntity {
  /** Always "_shared": the library belongs to the owner, not to a company */
  tenantId: string;
  ruleId: string;
  version: number;
  /** The full text of the rule in this version (its revision is the version number) */
  rule: BuiltInRule;
  changeNote: string;
  publishedBy: string;
  publishedByName?: string;
  publishedAt: string;
  /** This version retires the rule: it leaves the library, earlier versions stay for old records */
  retired?: boolean;
}

/** One switch of a rule on or off for a vessel: who, when, why, and which version of the rule it concerned. Never edited. */
export interface VesselRuleLog extends BaseEntity {
  tenantId: string;
  vesselId: string;
  ruleId: string;
  ruleVersion: number;
  action: "off" | "on";
  reason?: string;
  /** What the rule was for the vessel at the time (a mandatory rule switched off is a deviation) */
  obligation?: Obligation;
  by: string;
  byName?: string;
  at: string;
}

// ============================================
// WEATHER FOR A SAILING
// ============================================

/** One place in a forecast: the hours of the stay, how well the series covers them, and the range */
export interface ForecastPlace {
  role: "departure" | "destination";
  name: string;
  requested: { latitude: number; longitude: number };
  /** The grid point SMHI answered for; the forecast is for it, not for the exact spot */
  grid: { latitude: number; longitude: number };
  window: { from: string; to: string; coverage: "full" | "partial" | "none"; seriesFrom?: string; seriesTo?: string; wholeTrip: boolean };
  points: Array<{ validAt: string; windSpeed?: number; windFromDeg?: number; gust?: number; temperature?: number; precipitationMm?: number; visibilityKm?: number }>;
  summary: { windMin?: number; windMax?: number; gustMax?: number; windFromDeg?: number; temperatureMin?: number; temperatureMax?: number; precipitationMm?: number; hours: number };
}

/** What was available before departure: kept as it was fetched, never rewritten when SMHI updates the series */
export interface WeatherForecastSnapshot {
  id: string;
  tenantId: string;
  kind: "forecast";
  voyageId: string;
  vesselId: string;
  /** The revision of the trip's destination the forecast is for */
  destinationRevision: number;
  source: "SMHI snow1g";
  /** When SMHI made the forecast */
  referenceTime: string;
  fetchedAt: string;
  places: ForecastPlace[];
  /** A place that was wanted but could not be forecast, and why; the other place is still shown */
  missing?: Array<{ role: "departure" | "destination"; reason: string }>;
  /** The revision of the departure place the forecast is for (unset: the departure place had no position) */
  departureRevision?: number;
  /** What the forecast was made for (places, positions, revisions and times); a changed basis is a new forecast */
  basis?: string;
  /** Marine report and station measurements fetched alongside this forecast; immutable with this snapshot. */
  marine?: import("../weather-marine").MarineConditions;
  createdAt: string;
  updatedAt: string;
}

export type ForecastStatus = "ok" | "no_destination" | "no_forecast" | "error";

export interface VoyageForecast {
  status: ForecastStatus;
  snapshot?: WeatherForecastSnapshot;
  /** The latest forecast is older than the time it is refreshed after, or the source could not be asked */
  stale?: boolean;
  /** Why no forecast could be had now */
  error?: string;
  /** The forecast is kept as it was before departure: it is no longer refreshed */
  frozen?: boolean;
  /** A new fetch is allowed now (the source is not asked more often than it updates) */
  canRefresh?: boolean;
  /** The departure place has no coordinates, so only the destination is forecast */
  departureHasNoPosition?: boolean;
}

export type ObservationParameter = "windSpeed" | "windFromDeg" | "gust" | "temperature";
export type ObservationStatus = "available" | "partial" | "no_position" | "no_station" | "no_observation" | "stale" | "failed";

export interface StationObservation {
  value?: number;
  unit: string;
  stationId?: string;
  stationName?: string;
  distanceKm?: number;
  observedAt?: string;
  /** SMHI's quality code, kept as given */
  quality?: string;
  suspect?: boolean;
  status: "available" | "no_station" | "no_observation" | "stale" | "failed";
}

/** What was measured at the SMHI stations near the vessel at one time of a sailing */
export interface WeatherObservationSnapshot {
  id: string;
  tenantId: string;
  kind: "observation";
  voyageId: string;
  vesselId: string;
  moment: "departure" | "underway" | "arrival";
  targetAt: string;
  status: ObservationStatus;
  position?: { latitude: number; longitude: number; positionAt: string; ageMinutes: number; source: "ais" };
  values: Partial<Record<ObservationParameter, StationObservation>>;
  attempts: number;
  nextAttemptAt?: string;
  collectedAt?: string;
  journalEntryId?: string;
  /** Which SMHI period the values came from (the last day, the last four months or the archive) */
  period?: string;
  /** Collected afterwards for a sailing that had already left: the position and the observations are those of the time, not of now */
  historical?: boolean;
  /** 1 for the first collection; a revision follows the one it supersedes and the earlier one is kept */
  revision?: number;
  supersedes?: string;
  createdAt: string;
  updatedAt: string;
}

/** A stored job that completes the weather of a sailing afterwards, a few times at a time */
export interface WeatherJob {
  id: string;
  tenantId: string;
  kind: "job";
  voyageId: string;
  vesselId: string;
  status: "pending" | "running" | "done";
  requestedBy: string;
  requestedAt: string;
  /** Collect again and follow an earlier collection with a revision when the values differ */
  revise: boolean;
  total: number;
  /** How many of the times have been worked through */
  cursor: number;
  /** How many of them gave all the values */
  available: number;
  targetFrom: string;
  targetTo: string;
  finishedAt?: string;
  note?: string;
  createdAt: string;
  updatedAt: string;
}

export * from "./trip-review";

export interface VoyageWeather {
  observations: WeatherObservationSnapshot[];
  /** What the skipper saw, apart from the stations */
  skipper?: import("../weather-report").SkipperWeather[];
  job?: WeatherJob;
}
